// src/pages/Orders.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AllPaymentsThunk,
  DeleteInvoiceThunk,
  UpdateInvoiceThunk,
} from "../../redux/states/payments/thunk";
import NonAuthLayout from "../../Layout/NonAuthLayout";
import {
  Eye,
  Edit,
  Delete,
  Search,
  Filter,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  DollarSign,
  Calendar,
  Hash,
  ReceiptIndianRupee,
  ClipboardClock,
  BadgeIndianRupee,
  IndianRupee,
} from "lucide-react";
import ConfirmDeleteModal from "../../Modals/ConfirmDeleteModal";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

const PaymentsManage = () => {
  const dispatch = useDispatch();
  const {
    payments: paymentsState,
    loading,
    error,
  } = useSelector((state) => state.payments);

  const payments = paymentsState || [];
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const navigate = useNavigate();
  const [editingId, setEditingId] = useState(null); // current payment being edited
  const [statusValue, setStatusValue] = useState(""); // selected status

  useEffect(() => {
    dispatch(AllPaymentsThunk());
  }, [dispatch]);

  useEffect(() => {
    if (payments.length > 0) {
      const filtered = payments.filter(
        (payment) =>
          payment.order_id_data
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          payment.payment_status
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          payment.amount
            ?.toString()
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          payment.payment_method
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase())
      );
      setFilteredPayments(filtered);
    } else {
      setFilteredPayments([]);
    }
  }, [payments, searchTerm]);

  const handleDeleteClick = (uuid) => {
    setSelectedId(uuid);
    setModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedId) {
      dispatch(DeleteInvoiceThunk(selectedId))
        .then(() => {
          toast.success("Payment deleted successfully!");
          dispatch(AllPaymentsThunk()); // Refresh the list
        })
        .catch(() => {
          toast.error("Failed to delete payment");
        });
      setModalOpen(false);
      setSelectedId(null);
    }
  };

  const handleView = (uuid) => {
    navigate(`/view-payments/${uuid}`);
  };

  const handleEdit = (uuid) => {
    navigate(`/update-new-payments/${uuid}`);
  };

  const handleRefresh = () => {
    dispatch(AllPaymentsThunk());
    toast.success("Payments list refreshed!");
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
      case "success":
      case "active":
        return {
          color: "bg-green-100 text-green-800 border-green-200",
          icon: CheckCircle,
          className: "text-green-600",
        };
      case "failed":
      case "cancelled":
      case "inactive":
        return {
          color: "bg-red-100 text-red-800 border-red-200",
          icon: XCircle,
          className: "text-red-600",
        };
      case "pending":
      case "processing":
        return {
          color: "bg-yellow-100 text-yellow-800 border-yellow-200",
          icon: Clock,
          className: "text-yellow-600",
        };
      default:
        return {
          color: "bg-gray-100 text-gray-800 border-gray-200",
          icon: AlertCircle,
          className: "text-gray-600",
        };
    }
  };

  const getPaymentStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case "paid":
      case "completed":
        return "bg-green-100 text-green-800 border-green-200";
      case "pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "failed":
      case "cancelled":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const totalAmount = filteredPayments.reduce(
    (sum, payment) => sum + (parseFloat(payment.amount) || 0),
    0
  );
  const completedPayments = filteredPayments.filter(
    (p) => p.payment_status?.toLowerCase() === "completed"
  ).length;
  const pendingPayments = filteredPayments.filter(
    (p) => p.payment_status?.toLowerCase() === "pending"
  ).length;

  const handleEditStatus = (payment) => {
    setEditingId(payment.uuid);
    setStatusValue(payment.payment_status || "pending"); // default value
  };

  // 4️⃣ Handle save
const handleSaveStatus = (uuid) => {
  const formData = { payment_status: statusValue };
  dispatch(UpdateInvoiceThunk({ uuid, formData }))
    .unwrap() // ✅ unwrap promise for proper .then/.catch
    .then(() => {
      toast.success("Payment status updated!");
      setFilteredPayments((prev) =>
        prev.map((p) =>
          p.uuid === uuid ? { ...p, payment_status: statusValue } : p
        )
      );
      setEditingId(null);
    })
    .catch(() => {
      toast.error("Failed to update payment status");
    });
};

  return (
    <NonAuthLayout>
      <div className="min-h-screen  p-2">
        {/* Header Section */}
        <div className="mb-3">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div className="mb-4 md:mb-0">
              <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
                <ReceiptIndianRupee className="w-8 h-8 text-blue-600" />
                Payments Management
              </h1>
              <p className="text-gray-600 mt-2">List and manage all payments</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleRefresh}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-300"
              >
                <RefreshCw
                  className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-2">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Total Payments
                </p>
                <p className="text-3xl font-bold text-gray-800 mt-2">
                  {filteredPayments.length}
                </p>
              </div>
           
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Total Amount
                </p>
                <p className="text-3xl font-bold text-gray-800 mt-2">
                  ₹ {totalAmount.toLocaleString("en-IN")}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Completed Payments
                </p>
                <p className="text-3xl font-bold text-gray-800 mt-2">
                  {completedPayments}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Pending Payments
                </p>
                <p className="text-3xl font-bold text-gray-800 mt-2">
                  {pendingPayments}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-2">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="text-gray-400 w-5 h-5" />
                </div>
                <input
                  type="text"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                  placeholder="Search by Order ID, Payment Status, or Amount..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button className="flex items-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all duration-300">
                <Filter className="w-4 h-4" />
                Filter
              </button>
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    S.No
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Order Details
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Payment Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Payment Date
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mb-2" />
                        <p className="text-gray-600">Loading payments...</p>
                      </div>
                    </td>
                  </tr>
                ) : filteredPayments.length > 0 ? (
                  filteredPayments.map((payment, index) => {
                    const StatusBadge = getStatusBadge(payment.status);
                    const StatusIcon = StatusBadge.icon;

                    return (
                      <tr
                        key={payment.uuid}
                        className="hover:bg-gray-50 transition-colors duration-200 group"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {index + 1}
                          </div>
                        </td>

                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="bg-blue-100 p-2 rounded-lg mr-3">
                              <Hash className="w-4 h-4 text-blue-600" />
                            </div>
                            <div>
                              <div className="text-sm font-semibold text-gray-900">
                                {payment.order_id_data || "N/A"}
                              </div>
                              {payment.payment_method && (
                                <div className="text-xs text-gray-500 mt-1">
                                  {payment.payment_method}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingId === payment.uuid ? (
                            <div className="flex items-center gap-2">
                              <select
                                className="border border-gray-300 rounded-lg px-2 py-1 text-sm"
                                value={statusValue}
                                onChange={(e) => setStatusValue(e.target.value)}
                              >
                                <option value="pending">Pending</option>
                                <option value="completed">Completed</option>
                              </select>
                              <button
                                onClick={() => handleSaveStatus(payment.uuid)}
                                className="px-2 py-1 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 transition"
                              >
                                Update
                              </button>
                              <button
                                onClick={() => setEditingId(null)}
                                className="px-2 py-1 bg-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-400 transition"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span
                                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getPaymentStatusBadge(
                                  payment.payment_status
                                )}`}
                              >
                                {payment.payment_status || "N/A"}
                              </span>
                              <button
                                onClick={() => handleEditStatus(payment)}
                                className="p-1 text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors duration-200"
                                title="Edit Status"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <BadgeIndianRupee className="w-4 h-4 text-green-600 mr-1" />
                            <span className="text-sm font-semibold text-gray-900">
                              {payment.amount || "0"}
                            </span>
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${StatusBadge.color}`}
                          >
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {payment.status || "N/A"}
                          </span>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-900">
                            <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                            {payment.payment_date
                              ? new Date(
                                  payment.payment_date
                                ).toLocaleDateString("en-IN")
                              : "N/A"}
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex justify-center items-center gap-2 opacity-100 group-hover:opacity-100 transition-opacity duration-200">
                            <button
                              onClick={() => handleView(payment.uuid)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEdit(payment.uuid)}
                              className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors duration-200"
                              title="Edit Payment"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(payment.uuid)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                              title="Delete Payment"
                            >
                              <Delete className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center text-gray-500">
                        <AlertCircle className="w-12 h-12 mb-3 text-gray-400" />
                        <p className="text-lg font-medium mb-2">
                          No payments found
                        </p>
                        <p className="text-sm">
                          No payments matched your search criteria
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {filteredPayments.length > 0 && (
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <p className="text-sm text-gray-600">
                  Showing{" "}
                  <span className="font-semibold">
                    {filteredPayments.length}
                  </span>{" "}
                  payments
                </p>
                <div className="flex items-center gap-4 mt-2 md:mt-0">
                  <p className="text-sm text-gray-600">
                    Total Amount:{" "}
                    <span className="font-semibold text-green-600">
                      ₹{totalAmount.toLocaleString("en-IN")}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Delete Modal */}
        <ConfirmDeleteModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onConfirm={handleConfirmDelete}
          title="Delete Payment"
          message="Are you sure you want to delete this payment? This action cannot be undone."
        />
      </div>
    </NonAuthLayout>
  );
};

export default PaymentsManage;
